import { Button, Col, Container, Row } from "react-bootstrap"
import { navigateTo } from "../../redux/router/routerActions"
import { useAppDispatch } from "../../redux/store"
import QuickForm from "../../Components/Forms/QuickForm/QuickForm"
import { convertActiveQueryToUrl } from "../../Helpers/Query/queryHelpers";
import useActiveQueryHook from "../../Hooks/UseActiveQuery/useActiveQueryHook";
import NotificationEntry from "../../Components/NotificationEntry/NotificationEntry"
import { useBulkEditNotificationEntriesMutation, useDeleteNotificationEntryMutation, useGetNotificationEntriesQuery } from "../../services/bClientApi"
import MongoPagination from "../../Components/Pagination/MongoPagination"
import { NotificationEntryType } from "../../Types/notificationEntryType"
import { useState } from "react";
import { ConfirmModal } from "../../Components/Modals/confirmModal";

export default function NotificationEntriesPage(props: {
    baseQuery?: any,
    pageTitle?: string,
    baseSlug?: string,
}) {

    const { 
        baseQuery = {},
        pageTitle = 'Notifications Entries',
        baseSlug = 'notification-entries'
     } = props

    const [disableButtons, setDisableButtons] = useState(false)
    const [selectedNotifications, setSelectedNotifications] = useState<{ [key: string]: boolean }>({})
    const [activeConfirmationModal, setActiveConfirmationModal] = useState({
        active: false,
        title: '',
        message: '',
        onConfirm: () => { },
        onClose: () => { }
    })

    const resetConfirmationModal = () => {
        setActiveConfirmationModal({
            active: false,
            title: '',
            message: '',
            onConfirm: () => { },
            onClose: () => { }
        })
        setDisableButtons(false)
    }

    const [
        bulkEditNotificationEntries,
        {
            isLoading: isEditing,
            // error: editError,
        }
    ] = useBulkEditNotificationEntriesMutation()

    const [
        deleteNotificationEntry,
        {
            isLoading: deleteLoading,
            // error: deleteError
        }
    ] = useDeleteNotificationEntryMutation()

    const dispatch = useAppDispatch()

    const { activeQuery } = useActiveQueryHook({
        query: {},
        limit: 10,
    })

    const {
        data,
        error,
        isLoading,
    } = useGetNotificationEntriesQuery({
        notificationEntriesQuery: {
            ...activeQuery.query,
            // userId: _id,
            ...baseQuery
        },
        pagination: {
            page: activeQuery.page,
            limit: activeQuery.limit,
        }
    }, {
        skip: activeConfirmationModal.active || isEditing || deleteLoading || disableButtons
    })
    const notificationsList = data?.docs || []

    const loadPage = (pageParams: { query: any, page: number, limit: number }) => {
        // const destination = `/my-notifications?${convertActiveQueryToUrl(pageParams)}`
        const destination = `/${baseSlug}?${convertActiveQueryToUrl(pageParams)}`
        dispatch(navigateTo(destination))
    }

    const markSelectedAsRead = async () => {
        // disable buttons
        setDisableButtons(true)
        // change message of modal to marking as read...
        setActiveConfirmationModal(prev => ({
            ...prev,
            message: 'Marking as read...'
        }))
        // Call API to mark as read
        console.log('Marking as read:', selectedNotifications)
        const markedEntries = Object.keys(selectedNotifications).map(id => {
            return {
                _id: id,
                readDate: new Date(),
            }
        })
        // wait for 5 seconds
        await bulkEditNotificationEntries(markedEntries).catch(err => {
            console.error('Error marking notifications as read:', err)
        })
        setSelectedNotifications({})
        resetConfirmationModal()
    }

    const deleteSelected = async () => {
        // disable buttons
        setDisableButtons(true)
        // change message of modal to deleting...
        setActiveConfirmationModal(prev => ({
            ...prev,
            message: 'Deleting...'
        }))

        // Call API to delete notification
        const deleteList = Object.keys(selectedNotifications)
        for (let i = 0; i < deleteList.length; i++) {
            const id = deleteList[i]
            await deleteNotificationEntry(id).catch(err => {
                console.error('Error deleting notification:', err)
            })
        }
        setSelectedNotifications({})
        resetConfirmationModal()
    }

    return (
        <Container>
            <Row>
                <Col>
                    <h1>{pageTitle}</h1>
                    <hr />
                    {error && (<>{JSON.stringify(error)}<br /></>)}

                    <QuickForm
                        layout={{ xs: 2 }}
                        initialData={{
                            createdDateStart: activeQuery.query.createdDateStart,
                            createdDateEnd: activeQuery.query.createdDateEnd,
                            type: activeQuery.query.type,
                            message: activeQuery.query.message,
                            unRead: activeQuery.query.unRead,
                        }}
                        fields={[
                            {
                                fieldName: 'createdDateStart',
                                fieldLabel: 'Created Date Start',
                                fieldType: 'date',
                                placeholder: 'createdDateStart',
                                layout: {
                                    xs: 12,
                                    md: 6,
                                }
                            },
                            {
                                fieldName: 'createdDateEnd',
                                fieldLabel: 'Created Date End',
                                fieldType: 'date',
                                placeholder: 'createdDateEnd',
                                layout: {
                                    xs: 12,
                                    md: 6,
                                }
                            },
                            {
                                fieldName: 'type',
                                fieldLabel: 'Type',
                                fieldType: 'text',
                                placeholder: 'type',
                                layout: {
                                    xs: 12,
                                    md: 6,
                                }
                            },
                            {
                                fieldName: 'message',
                                fieldLabel: 'Message',
                                fieldType: 'text',
                                placeholder: 'message',
                                layout: {
                                    xs: 12,
                                    md: 6,
                                }
                            },
                            {
                                fieldName: 'unRead',
                                fieldLabel: 'Unread',
                                fieldType: 'checkbox',
                                placeholder: 'unRead',
                            }
                        ]}
                        disabled={isLoading}
                        onSubmit={(values: any) => {
                            const newQuery = {
                                ...values,
                            }
                            if (!values.unRead) {
                                delete newQuery.unRead
                            }
                            loadPage({
                                query: newQuery,
                                page: 1,
                                limit: activeQuery.limit
                            })
                        }}
                    />

                    {/* select all button */}
                    {notificationsList && notificationsList.length > 0 && (
                        <Button
                            variant="secondary"
                            size="sm"
                            className="me-2 mb-2"
                            onClick={() => {
                                if (Object.keys(selectedNotifications).length === notificationsList.length) {
                                    setSelectedNotifications({})
                                } else {
                                    const newSelected: { [key: string]: boolean } = {}
                                    notificationsList.forEach((notification: NotificationEntryType) => {
                                        if (notification._id) {
                                            newSelected[notification._id] = true
                                        }
                                    })
                                    setSelectedNotifications(newSelected)
                                }
                            }}
                        >
                            {Object.keys(selectedNotifications).length === notificationsList.length ? 'Deselect All' : 'Select All'}
                        </Button>
                    )}

                    {selectedNotifications && Object.keys(selectedNotifications).length > 0 && (
                        <>
                            {/* mark as read */}
                            <Button
                                variant="secondary"
                                size="sm"
                                className="me-2 mb-2"
                                onClick={() => {
                                    // Handle mark as read action
                                    setActiveConfirmationModal({
                                        active: true,
                                        title: 'Mark as Read',
                                        message: `Are you sure you want to mark ${Object.keys(selectedNotifications).length} notification(s) as read?`,
                                        onConfirm: markSelectedAsRead,
                                        onClose: () => {
                                            resetConfirmationModal()
                                        }
                                    })
                                }}
                            >
                                Mark as Read
                            </Button>

                            {/* delete */}
                            <Button
                                variant="danger"
                                size="sm"
                                className="me-2 mb-2"
                                onClick={() => {
                                    // Handle delete action
                                    setActiveConfirmationModal({
                                        active: true,
                                        title: 'Delete Notification',
                                        message: `Are you sure you want to delete this notification?`,
                                        onConfirm: deleteSelected,
                                        onClose: () => {
                                            resetConfirmationModal()
                                        }
                                    })
                                }}
                            >
                                Delete
                            </Button>
                        </>
                    )}

                    <div className="notification-list-container">
                        {notificationsList && notificationsList.length > 0 && notificationsList.map((notificationEntry: NotificationEntryType, idx: number) => {
                            const isSelected = notificationEntry._id ? !!selectedNotifications[notificationEntry._id] : false
                            return (
                                <NotificationEntry
                                    key={`notification-entry-${idx}-${notificationEntry._id}`}
                                    notificationData={notificationEntry}
                                    customButtons={[
                                        {
                                            text: isSelected ? 'Deselect' : 'Select',
                                            variant: isSelected ? 'danger' : 'primary',
                                            onClick: (notificationData: NotificationEntryType) => {
                                                if (!notificationData._id) return
                                                if (selectedNotifications[notificationData._id]) {
                                                    const newSelected = { ...selectedNotifications }
                                                    delete newSelected[notificationData._id]
                                                    setSelectedNotifications(newSelected)
                                                } else {
                                                    setSelectedNotifications({
                                                        ...selectedNotifications,
                                                        [notificationData._id]: true,
                                                    })
                                                }
                                            }
                                        }
                                    ]}
                                />
                            )
                        })
                        }
                    </div>

                    <MongoPagination
                        data={data}
                        loadPage={(pageLoadInfo: { page: number, limit?: number }) => {
                            loadPage({
                                query: activeQuery.query,
                                limit: activeQuery.limit,
                                ...pageLoadInfo
                            })
                        }}
                    />
                </Col>
            </Row>

            <ConfirmModal
                active={activeConfirmationModal.active}
                title={activeConfirmationModal.title}
                message={activeConfirmationModal.message}
                onConfirm={() => {
                    activeConfirmationModal.onConfirm()
                }}
                onClose={() => {
                    activeConfirmationModal.onClose()
                    // resetConfirmationModal()
                }}
                disabled={isEditing || deleteLoading || disableButtons}
            />
        </Container>
    )
}